import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, Send } from 'lucide-react';
import { fairManagerRequestApi } from '../services/api';
import { FairManagerRequestCreate, FairManagerRequestResponse } from '../types';
import { decodeHtmlEntities } from '../utils/security';
import { isFairManager } from '../utils/rbac';
import { extractApiError } from '../utils/errorUtils';

export default function FairManagerRequestPage() {
  const navigate = useNavigate();
  const [existingRequest, setExistingRequest] = useState<FairManagerRequestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState<FairManagerRequestCreate>({
    business_name: '',
    business_description: '',
    website: '',
    phone: '',
    request_reason: '',
  });

  // Load existing request on mount
  useEffect(() => {
    loadExistingRequest();
  }, []);

  const loadExistingRequest = async () => {
    try {
      setLoading(true);
      const response = await fairManagerRequestApi.getMyRequest();
      setExistingRequest(response);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      // 404 means no request exists, which is fine
      if (apiError.status !== 404) {
        console.error('Error loading request:', apiError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.business_name.trim()) {
      setError('Business name is required');
      return;
    }
    if (!formData.request_reason.trim()) {
      setError('Request reason is required');
      return;
    }
    if (formData.business_name.length > 255) {
      setError('Business name must be 255 characters or less');
      return;
    }
    if (formData.request_reason.length > 500) {
      setError('Request reason must be 500 characters or less');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fairManagerRequestApi.submitRequest(formData);
      setExistingRequest(response);
      setSuccess('Request submitted successfully! Admins will review your application.');
      // Clear form
      setFormData({
        business_name: '',
        business_description: '',
        website: '',
        phone: '',
        request_reason: '',
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error submitting request:', apiError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStartNewRequest = () => {
    setExistingRequest(null);
    setSuccess('');
    setError('');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Already a Fair Manager
  if (isFairManager()) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              You're Already a Fair Manager!
            </h1>
            <p className="text-gray-600 mb-6">
              You have access to the Fair Manager dashboard where you can create and manage fairs.
            </p>
            <button
              onClick={() => navigate('/fairmanager/fairs')}
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark"
            >
              Go to Fair Manager Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show existing request status
  if (existingRequest) {
    const statusConfig = {
      pending: {
        icon: Clock,
        color: 'yellow',
        title: 'Request Under Review',
        message: 'Your Fair Manager role request is currently being reviewed by administrators.',
      },
      approved: {
        icon: CheckCircle,
        color: 'green',
        title: 'Request Approved!',
        message: 'Congratulations! You now have Fair Manager access. You can create and manage fairs.',
      },
      rejected: {
        icon: XCircle,
        color: 'red',
        title: 'Request Not Approved',
        message: 'Your Fair Manager role request was not approved at this time.',
      },
    };

    const config = statusConfig[existingRequest.status];
    const Icon = config.icon;

    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-6">
              <Icon className={`h-16 w-16 text-${config.color}-500 mx-auto mb-4`} />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{config.title}</h1>
              <p className="text-gray-600">{config.message}</p>
            </div>

            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Request Details</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Business Name</label>
                  <p className="text-gray-900">{decodeHtmlEntities(existingRequest.business_name)}</p>
                </div>
                {existingRequest.business_description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <p className="text-gray-900">{decodeHtmlEntities(existingRequest.business_description)}</p>
                  </div>
                )}
                {existingRequest.website && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website</label>
                    <a
                      href={existingRequest.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:underline"
                    >
                      {existingRequest.website}
                    </a>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Request Reason</label>
                  <p className="text-gray-900">{decodeHtmlEntities(existingRequest.request_reason)}</p>
                </div>
                {existingRequest.admin_notes && existingRequest.status === 'rejected' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Admin Notes</label>
                    <p className="text-gray-900">{decodeHtmlEntities(existingRequest.admin_notes)}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Submitted</label>
                  <p className="text-gray-900">
                    {new Date(existingRequest.requested_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {existingRequest.status === 'approved' && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/fairmanager/fairs')}
                  className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark"
                >
                  Go to Fair Manager Dashboard
                </button>
              </div>
            )}

            {existingRequest.status === 'rejected' && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleStartNewRequest}
                  className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark"
                >
                  Submit New Request
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show request form
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Become a Fair Manager
            </h1>
            <p className="text-gray-600">
              Submit a request to gain access to create and manage artist fairs on our platform.
            </p>
          </div>

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-800 rounded-md p-4">
              <CheckCircle className="inline h-5 w-5 mr-2" />
              {success}
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-1">
                Business/Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                maxLength={255}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                placeholder="e.g., Portland Art Fair Collective"
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.business_name.length}/255 characters
              </p>
            </div>

            <div>
              <label htmlFor="business_description" className="block text-sm font-medium text-gray-700 mb-1">
                Business Description
              </label>
              <textarea
                id="business_description"
                name="business_description"
                value={formData.business_description}
                onChange={handleInputChange}
                maxLength={1000}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                placeholder="Tell us about your organization or business..."
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.business_description?.length || 0}/1000 characters
              </p>
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                maxLength={20}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="request_reason" className="block text-sm font-medium text-gray-700 mb-1">
                Why do you want to become a Fair Manager? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="request_reason"
                name="request_reason"
                value={formData.request_reason}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                placeholder="Describe your experience managing events, fairs, or markets..."
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.request_reason.length}/500 characters
              </p>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
